import { useState } from 'react'
import { createFamily, joinFamily } from '@core/session'
import { Button, Card, ErrorNote, Field, Screen } from '@design/components'

/**
 * Shown to a signed-in user who belongs to no family yet.
 *
 * Two paths, deliberately unequal in prominence: creating a family is a
 * once-ever action for the administrator, joining is what the parents do. The
 * parent path is the one that must be trivial, because it happens on their
 * phone with someone reading a code to them.
 */
/** create_invite() in migration 0004 generates exactly this many characters. */
const CODE_LENGTH = 6

export function Setup({ onDone }: { onDone: () => void }) {
  const [mode, setMode] = useState<'choose' | 'create' | 'join'>('choose')

  if (mode === 'create') return <CreateFamily onDone={onDone} onBack={() => setMode('choose')} />
  if (mode === 'join') return <JoinFamily onDone={onDone} onBack={() => setMode('choose')} />

  return (
    <Screen className="flex flex-col justify-center max-w-md mx-auto w-full pad-safe-top">
      <h1 className="text-2xl font-semibold mb-1">Bijna klaar</h1>
      <p className="text-muted mb-6">Heb je een code gekregen, of begin je nieuw?</p>

      <div className="space-y-3">
        <Button size="large" onClick={() => setMode('join')}>
          Ik heb een code
        </Button>
        <Button size="large" tone="secondary" onClick={() => setMode('create')}>
          Nieuwe familie starten
        </Button>
      </div>
    </Screen>
  )
}

function JoinFamily({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await joinFamily(code)
      onDone()
    } catch (err) {
      setError(
        String(err).includes('invalid or expired')
          ? 'Die code klopt niet of is verlopen. Vraag een nieuwe.'
          : 'Er ging iets mis. Probeer het opnieuw.',
      )
      setBusy(false)
    }
  }

  return (
    <Screen className="flex flex-col justify-center max-w-md mx-auto w-full pad-safe-top">
      <h1 className="text-2xl font-semibold mb-1">Code invullen</h1>
      <p className="text-muted mb-6">De code van {CODE_LENGTH} tekens die je gekregen hebt.</p>
      <Card>
        <form onSubmit={submit}>
          <Field
            label="Code"
            value={code}
            // Uppercase and unspaced as you type: the code is read aloud over
            // the phone, so whatever the parent types should just work.
            // Strip anything that is not part of a code as you type: spaces,
            // hyphens, and the dashes a phone keyboard likes to insert. The
            // code gets read aloud down the phone, so whatever a parent hears
            // and types should just work.
            //
            // maxLength is deliberately NOT the code length. A hard cap equal
            // to the answer means one stray character silently blocks the last
            // real one, and the field just stops accepting input with nothing
            // on screen to explain why.
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            maxLength={CODE_LENGTH * 2}
            required
            placeholder="ABC234"
          />
          <ErrorNote>{error}</ErrorNote>
          <Button type="submit" size="large" disabled={busy || code.length < CODE_LENGTH}>
            {busy ? 'Bezig…' : 'Aanmelden bij de familie'}
          </Button>
        </form>
      </Card>
      <div className="mt-4">
        <Button tone="quiet" onClick={onBack}>
          Terug
        </Button>
      </div>
    </Screen>
  )
}

function CreateFamily({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const [name, setName] = useState('Familie')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await createFamily(name, displayName)
      onDone()
    } catch {
      setError('Er ging iets mis. Probeer het opnieuw.')
      setBusy(false)
    }
  }

  return (
    <Screen className="flex flex-col justify-center max-w-md mx-auto w-full pad-safe-top">
      <h1 className="text-2xl font-semibold mb-1">Nieuwe familie</h1>
      <p className="text-muted mb-6">
        Je wordt beheerder. Daarna kan je je ouders uitnodigen.
      </p>
      <Card>
        <form onSubmit={submit}>
          <Field
            label="Naam van de familie"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Field
            label="Jouw naam"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            hint="Dit zien je ouders bij ‘… heeft het gezien’."
            required
          />
          <ErrorNote>{error}</ErrorNote>
          <Button type="submit" disabled={busy || !displayName.trim()}>
            {busy ? 'Bezig…' : 'Familie aanmaken'}
          </Button>
        </form>
      </Card>
      <div className="mt-4">
        <Button tone="quiet" onClick={onBack}>
          Terug
        </Button>
      </div>
    </Screen>
  )
}
